import React, { useEffect, useState } from "react";
import {
  Box, Chip, CircularProgress, Grid, Stack, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import ChairOutlinedIcon from "@mui/icons-material/ChairOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, EmptyState, StatusChip } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

const CATEGORY_ICON = {
  Laptop: <ComputerOutlinedIcon />,
  Desktop: <ComputerOutlinedIcon />,
  Monitor: <MonitorOutlinedIcon />,
  Peripheral: <KeyboardOutlinedIcon />,
  Furniture: <ChairOutlinedIcon />,
};

const CATEGORY_ACCENTS = {
  Laptop: "#6C50BF",
  Desktop: "#5C85FF",
  Monitor: "#39C645",
  Peripheral: "#F9BC06",
  Furniture: "#FF6B6B",
  Other: "#14B8A6",
};

export default function MyInventoriesNew() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/inventory/getmyinventories");
        setItems(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, []);

  const totalValue = items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

  return (
    <Box p={3}>
      <PageHeader
        title="My Assets"
        subtitle="Inventory items assigned to you"
        icon={<DevicesOutlinedIcon />}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={4}>
          <StatCard label="Assigned Items" value={items.length} icon={<Inventory2OutlinedIcon />} accent="#6C50BF" />
        </Grid>
        <Grid item xs={6} md={4}>
          <StatCard label="Total Value" value={`₹${totalValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} accent="#39C645" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard label="Categories" value={[...new Set(items.map((i) => i.category))].length} accent="#F9BC06" />
        </Grid>
      </Grid>

      <SectionCard title="Assigned Inventory" subtitle={`${items.length} items`}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <EmptyState title="No assets assigned" subtitle="You don't have any inventory items assigned to you yet." />
        ) : (
          <Grid container spacing={2}>
            {items.map((item) => {
              const accent = CATEGORY_ACCENTS[item.category] || CATEGORY_ACCENTS.Other;
              const icon = CATEGORY_ICON[item.category] || <Inventory2OutlinedIcon />;
              return (
                <Grid item xs={12} sm={6} md={4} key={item._id || item.itemID}>
                  <Box
                    sx={{
                      borderRadius: 3, overflow: "hidden",
                      border: "1px solid", borderColor: "divider",
                      transition: "all 0.2s",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
                    }}
                  >
                    <Box sx={{ height: 5, bgcolor: accent }} />
                    <Box sx={{ p: 2.5 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: `${accent}15`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.25 }}>{item.itemName}</Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>{item.brandModel || "—"}</Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Chip label={item.category} size="small" sx={{ bgcolor: `${accent}15`, color: accent, fontWeight: 600, height: 22 }} />
                            <StatusChip status={item.status || "Assigned"} />
                          </Stack>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={3} sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }} flexWrap="wrap" useFlexGap>
                        <Box>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>Serial</Typography>
                          <Typography sx={{ fontSize: 12, fontFamily: "monospace" }}>{item.serialNumber || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>Since</Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {(() => {
                              const hist = item.assignmentHistory;
                              const date = hist && hist.length > 0
                                ? hist[hist.length - 1].assignmentDate
                                : null;
                              return date ? new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
                            })()}
                          </Typography>
                        </Box>
                        {item.warrantyExpiryDate && (
                          <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>Warranty</Typography>
                            <Typography sx={{ fontSize: 12 }}>
                              {new Date(item.warrantyExpiryDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </SectionCard>
    </Box>
  );
}
